/**
 * Type scale for the mailed documents — the invoice slip (`components/print/invoice-slip.tsx`),
 * the account statement (`components/accounting/reports/statement-sheet.tsx`) and the
 * management-company invoice (`components/print/sbs-invoice-slip.tsx`). Sizes are CSS px
 * (RN-Web), so 12 = 9pt on paper.
 *
 * The invoice and statement are tri-folded into a #10 double-window envelope: the org block
 * shows through the top window and the To box through the bottom one. The To row's position is
 * pinned by `ENVELOPE` / `headerMinHeight`, not by the header's content — see below.
 */
export const PRINT_FONT = {
	/** Association name, first line of the return-address block. */
	orgFirstLine: 15,
	orgLine: 14,
	/** Management-company name heading the SBS invoice letterhead (no envelope window). */
	letterhead: 17,
	title: 30,
	stampLabel: 11,
	stampValue: 13,
	/** "To" / "Regarding" / "Bill To" header cells. */
	boxLabel: 13,
	/** Recipient name + mailing address lines in the To box. */
	address: 13,
	regarding: 13,
	callout: 12,
	remit: 16,
	/** Ledger / line-item table cells, header row included. */
	cell: 12,
	balanceLabel: 13,
	balanceValue: 14,
	memoLabel: 11,
	memoText: 13,
	legal: 10,
	/** The invoice's terms (its legal paragraph), in the call-out box under the line items. */
	terms: 16,
	contactLabel: 11,
	contactValue: 12,
} as const;

/**
 * Explicit line heights, so box sizes don't vary with the platform font's `normal` line height.
 * The header ones equal the height each element had before the type scale was raised.
 */
export const PRINT_LINE = {
	orgFirstLine: 17,
	orgLine: 16,
	title: 33,
	/** Stamp rows and the To / Regarding header cells: 15 + 2×2 padding + 2 border = 21. */
	boxRow: 15,
	/** 4 × 14 + 6 + 10 padding + 1 border = the pre-scale 4-line To box (4 × 13 + 2 × 10 + 1). */
	address: 14,
	legal: 13.5,
	terms: 21,
	/** Table cells: with 2.5px padding top + bottom and a 0.5px rule, a row is `PRINT_ROW_HEIGHT`. */
	cell: 15.5,
} as const;

/** One line-item / filler row: `PRINT_LINE.cell` + 2 × 2.5 padding + 0.5 rule. */
export const PRINT_ROW_HEIGHT = 21;

/** Vertical padding that pairs with `PRINT_LINE.boxRow` to keep a 21px header cell. */
export const PRINT_BOX_ROW_PAD = 2;

export const PX_PER_IN = 96;

/**
 * #10 double-window envelope alignment, in inches from the paper's top edge (letter, tri-folded).
 *
 * `toTopIn` is the top of the To / Regarding label row. It used to fall wherever the header's
 * content ended, and a physical fold test after the 2.7.1 type bump found the address too low in
 * the window. Now the header reserves `headerMinHeight` so the row starts here on every invoice
 * and statement: the address's first line at ~2.48in, a 4-line address ending ~3.07in and a
 * 5-line one ~3.21in, inside the standard recipient window (2.375–3.500in) with room either way.
 */
export const ENVELOPE = {
	toTopIn: 2.2,
	/** The two tri-fold lines on a letter sheet. */
	foldsIn: [11 / 3, 22 / 3],
} as const;

/** Extra left indent of the address lines inside the To box (the box itself doesn't move). */
export const TO_ADDRESS_INDENT = 10;

/** Gap between the header and the To / Regarding row. */
export const TO_ROW_GAP = 8;

/**
 * Header min-height that lands the To row on `ENVELOPE.toTopIn`, given where the sheet starts on
 * paper. A header with more content than this (a wrapped association name under a tall logo)
 * still pushes the row down rather than overlapping it.
 */
export const headerMinHeight = (sheetTopIn: number): number =>
	Math.round((ENVELOPE.toTopIn - sheetTopIn) * PX_PER_IN) - TO_ROW_GAP;

/**
 * `@page` left/right margin for the mailed documents. Narrower than the content inset on purpose:
 * the sheet pads itself back in (`*_SHEET_SIDE_PAD_IN`) so the content stays where it was, and the
 * fold ticks sit in that padding, clear of the content. (Ticks positioned out into the page margin
 * instead make Chrome shrink the whole page to fit, which moves the address out of its window.)
 */
export const PRINT_SIDE_MARGIN_IN = 0.15;

/** Invoice print page: letter; the slip's content inset 0.5in from every paper edge. */
export const INVOICE_SHEET_TOP_IN = 0.5;
export const INVOICE_SHEET_SIDE_PAD_IN = 0.5 - PRINT_SIDE_MARGIN_IN;
export const INVOICE_PAGE_CSS = `@page { size: letter; margin: ${INVOICE_SHEET_TOP_IN}in ${PRINT_SIDE_MARGIN_IN}in; }`;
/** Print padding for the invoice slip (`[data-print-slip]`). */
export const INVOICE_SHEET_PRINT_PADDING = `0 ${INVOICE_SHEET_SIDE_PAD_IN}in`;

/** Statement print page: `@page` margins (in) and the sheet's padding in print. */
export const STATEMENT_PAGE_MARGIN = { top: 0.45, right: PRINT_SIDE_MARGIN_IN, bottom: 0.55, left: PRINT_SIDE_MARGIN_IN } as const;
export const STATEMENT_PRINT_PAD_TOP = 20;
export const STATEMENT_SHEET_SIDE_PAD_IN = 0.45 - PRINT_SIDE_MARGIN_IN;
export const STATEMENT_SHEET_PRINT_PADDING = `${STATEMENT_PRINT_PAD_TOP}px ${STATEMENT_SHEET_SIDE_PAD_IN}in 0 ${STATEMENT_SHEET_SIDE_PAD_IN}in`;
export const STATEMENT_SHEET_TOP_IN = STATEMENT_PAGE_MARGIN.top + STATEMENT_PRINT_PAD_TOP / PX_PER_IN;
